import { AlertProps } from '@mui/material';
import { FortnoxImportEntityResult, FortnoxImportSummary } from '../models/FortnoxImportSummary';

/** Swedish names for the entity keys the backend reports, singular and plural. */
const ENTITY_NAMES: Record<string, { one: string; many: string }> = {
  customers: { one: 'kund', many: 'kunder' },
  articles: { one: 'artikel', many: 'artiklar' },
  invoices: { one: 'faktura', many: 'fakturor' },
  employees: { one: 'anställd', many: 'anställda' },
};

const name = (key: string, count: number): string => {
  const names = ENTITY_NAMES[key];
  if (!names) {
    return key;
  }
  return count === 1 ? names.one : names.many;
};

/** Anything the organization was supposed to get and did not. */
const missingFrom = (result: FortnoxImportEntityResult): number =>
  result.failed + result.notAttempted;

/**
 * What to say when Fortnox sends the person back without approving the connection. Fortnox
 * does that when the login is cancelled, or when the Fortnox user may not add integrations,
 * and says which in the `error` and `error_description` it adds to the address.
 */
export const buildFortnoxConnectErrorMessage = (
  error: string | null,
  description: string | null
): string =>
  error === 'access_denied'
    ? 'Kopplingen till Fortnox godkändes inte. Det händer om inloggningen avbryts, eller om Fortnox-användaren saknar behörighet att lägga till integrationer. Be någon med administratörsbehörighet i Fortnox att koppla, eller försök igen.'
    : `Kopplingen till Fortnox gick inte igenom${description ? `: ${description}` : ''}. Försök igen.`;

/**
 * Turns an import summary into something a person can act on.
 *
 * A partial import is reported as a warning rather than an error: most of the data did
 * arrive, and the run can simply be repeated. What matters is that it is never reported as
 * a success, and that the message says how much is missing rather than that "something went
 * wrong".
 */
export const buildFortnoxImportAlert = (
  summary: FortnoxImportSummary
): { severity: AlertProps['severity']; message: string; seconds: number } => {
  if (summary.status === 'complete') {
    const imported = Object.entries(summary.entities)
      .filter(([, result]) => result.imported > 0)
      .map(([key, result]) => `${result.imported} ${name(key, result.imported)}`)
      .join(', ');

    return {
      severity: 'success',
      message: imported
        ? `Import från Fortnox klar: ${imported}.`
        : 'Import från Fortnox klar. Det fanns inget att hämta.',
      seconds: 5,
    };
  }

  const missing = Object.entries(summary.entities)
    .filter(([, result]) => missingFrom(result) > 0)
    .map(([key, result]) => {
      const count = missingFrom(result);
      return `${count} av ${result.fromFortnox} ${name(key, result.fromFortnox)}`;
    })
    .join(', ');

  return {
    severity: 'warning',
    message: missing
      ? `Importen från Fortnox blev ofullständig: ${missing} hämtades inte. Kör importen igen, och kontakta supporten om det upprepas.`
      : 'Importen från Fortnox blev ofullständig. Kör importen igen, och kontakta supporten om det upprepas.',
    // Long enough to be read and acted on: this is the message that stops a company
    // believing it has data it does not have.
    seconds: 20,
  };
};
